import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, timer, from, of, Subject, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap, takeUntil, mergeMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { NotificationWebSocketService } from './notification-websocket.service';
import {
  Notification,
  NotificationDetail,
  NotificationCount,
  NotificationListResponse,
  NotificationFilters,
  NotificationsSummary,
  MarkAllAsReadResult,
  BatchNotificationActionRequest,
  BatchNotificationAction,
  BatchOperationResult,
  PostponeNotificationResult,
  CancelPostponeResponse,
  Tag,
  TagListResponse,
  PinNotificationResult,
  ConnectionState,
} from '../models/notification.model';
import {
  NotificationTranslations,
  NotificationTranslation,
} from '../models/notification-translation.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private apiUrl = `${environment.cafler_backoffice_back_url}/v1`;
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  private newNotificationSubject = new Subject<Notification>();
  public newNotification$ = this.newNotificationSubject.asObservable();
  private postponementChangedSubject = new Subject<void>();
  public postponementChanged$ = this.postponementChangedSubject.asObservable();
  private autoRefreshInterval = 30000;
  private autoRefresh$?: Observable<NotificationCount>;
  private destroy$ = new Subject<void>();
  private isWebSocketConnected = false;
  private currentFilters?: NotificationFilters;

  public connectionState$: Observable<ConnectionState>;
  public settings$ = this.webSocketService.settings$;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private webSocketService: NotificationWebSocketService,
    private ngZone: NgZone
  ) {
    this.connectionState$ = this.webSocketService.connectionState$;
    this.setupWebSocketSubscriptions();
  }

  private setupWebSocketSubscriptions(): void {
    this.webSocketService.connectionState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.isWebSocketConnected = state === 'connected';
    });

    this.webSocketService.notificationReceived$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => {
        this.ngZone.run(() => {
          const currentNotifications = this.notificationsSubject.value;
          const exists = currentNotifications.some((n) => n.id === notification.id);
          if (!exists) {
            this.notificationsSubject.next([notification, ...currentNotifications]);
          }
          this.newNotificationSubject.next(notification);
        });
      });

    this.webSocketService.notificationRead$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.ngZone.run(() => {
        const notifications = this.notificationsSubject.value;
        const updatedNotifications = notifications.map((n) =>
          n.id === event.notificationId ? { ...n, isRead: true, readAt: event.readAt } : n
        );
        this.notificationsSubject.next([...updatedNotifications]);
      });
    });

    this.webSocketService.allNotificationsRead$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const notifications = this.notificationsSubject.value;
      const updatedNotifications = notifications.map((n) => ({ ...n, isRead: true }));
      this.notificationsSubject.next(updatedNotifications);
      this.unreadCountSubject.next(0);
    });

    this.webSocketService.counterUpdated$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.ngZone.run(() => {
        this.unreadCountSubject.next(event.unreadCount);
      });
    });

    this.webSocketService.notificationArchived$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        const notifications = this.notificationsSubject.value;
        const updatedNotifications = notifications.filter((n) => n.id !== event.notificationId);
        this.notificationsSubject.next(updatedNotifications);
      });

    this.webSocketService.notificationUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.ngZone.run(() => {
          const notifications = this.notificationsSubject.value;
          const notification = notifications.find((n) => n.id === event.notificationId);
          if (notification) {
            const updatedNotifications = notifications.map((n) =>
              n.id === event.notificationId
                ? { ...n, isRead: false, readAt: undefined, isPostponed: false, postponedUntil: null }
                : n
            );
            this.notificationsSubject.next([...updatedNotifications]);
          }
        });
      });

    this.webSocketService.postponementChanged$
      .pipe(
        takeUntil(this.destroy$),
        mergeMap(() => this.getNotificationCount())
      )
      .subscribe();
  }

  async connectWebSocket(): Promise<void> {
    await this.webSocketService.connect();
  }

  async disconnectWebSocket(): Promise<void> {
    await this.webSocketService.disconnect();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getNotificationCount(): Observable<NotificationCount> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.warn('[NotificationService] No token available, user may not be authenticated');
          return of({ unreadCount: 0 });
        }
        return this.http.get<NotificationCount>(`${this.apiUrl}/notifications/count`, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      }),
      tap((response) => {
        this.unreadCountSubject.next(response.unreadCount);
      }),
      catchError((error) => {
        console.error('[NotificationService] Error fetching notification count:', error);
        return of({ unreadCount: 0 });
      })
    );
  }

  getNotifications(
    page: number = 1,
    pageSize: number = 25,
    filters?: NotificationFilters,
    append = false
  ): Observable<NotificationListResponse> {
    if (!append) {
      this.currentFilters = filters;
    }
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.warn('[NotificationService] No token available, user may not be authenticated');
          return of({
            items: [],
            pagination: { currentPage: 1, pageSize: 25, totalItems: 0, totalPages: 0 },
          });
        }

        let params = new HttpParams()
          .set('page', page.toString())
          .set('pageSize', pageSize.toString());

        if (filters) {
          if (filters.userId !== null && filters.userId !== undefined) {
            params = params.set('userId', filters.userId.toString());
          }
          if (filters.searchText) {
            params = params.set('searchText', filters.searchText);
          }
          if (filters.category !== null && filters.category !== undefined) {
            params = params.set('category', filters.category.toString());
          }
          if (filters.type !== null && filters.type !== undefined) {
            params = params.set('type', filters.type.toString());
          }
          if (filters.priority !== null && filters.priority !== undefined) {
            params = params.set('priority', filters.priority.toString());
          }
          if (filters.status !== null && filters.status !== undefined) {
            params = params.set('status', filters.status.toString());
          }
          if (filters.dateFrom) {
            params = params.set('dateFrom', filters.dateFrom.toISOString());
          }
          if (filters.dateTo) {
            params = params.set('dateTo', filters.dateTo.toISOString());
          }
          if (filters.read !== null && filters.read !== undefined) {
            params = params.set('read', filters.read.toString());
          }
          if (filters.postponed) {
            params = params.set('postponed', filters.postponed);
          }
          if (filters.tags && filters.tags.length > 0) {
            filters.tags.forEach((tag) => {
              params = params.append('tags', tag);
            });
          }
          if (filters.sortBy !== null && filters.sortBy !== undefined) {
            params = params.set('sortBy', filters.sortBy.toString());
          }
          if (filters.sortOrder) {
            params = params.set('sortOrder', filters.sortOrder);
          }
        }

        return this.http.get<NotificationListResponse>(`${this.apiUrl}/notifications`, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params,
        });
      }),
      tap((response) => {
        if (append) {
          const currentNotifications = this.notificationsSubject.value;
          const newItems = response.items.filter(
            (newItem) => !currentNotifications.some((existing) => existing.id === newItem.id)
          );
          this.notificationsSubject.next([...currentNotifications, ...newItems]);
        } else {
          this.notificationsSubject.next(response.items);
        }
      }),
      catchError((error) => {
        console.error('[NotificationService] Error fetching notifications:', error);
        return of({
          items: [],
          pagination: { currentPage: 1, pageSize: 25, totalItems: 0, totalPages: 0 },
        });
      })
    );
  }

  getNotificationById(id: string): Observable<NotificationDetail | null> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.warn('[NotificationService] No token available, cannot get notification');
          return of(null);
        }
        return this.http.get<NotificationDetail>(`${this.apiUrl}/notifications/${id}`, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      }),
      catchError((error) => {
        console.error('[NotificationService] Error fetching notification:', error);
        return of(null);
      })
    );
  }

  markAsRead(notificationId: string): Observable<NotificationDetail | null> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.warn('[NotificationService] No token available, cannot mark as read');
          return of(null);
        }
        return this.http.patch<NotificationDetail>(
          `${this.apiUrl}/notifications/${notificationId}/read`,
          {},
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      }),
      tap((notification) => {
        if (!this.isWebSocketConnected && notification) {
          const currentCount = this.unreadCountSubject.value;
          if (currentCount > 0) {
            this.unreadCountSubject.next(currentCount - 1);
          }
          const notifications = this.notificationsSubject.value;
          const updatedNotifications = notifications.map((n) =>
            n.id === notificationId ? { ...n, isRead: true } : n
          );
          this.notificationsSubject.next(updatedNotifications);
        }
      }),
      catchError((error) => {
        console.error('[NotificationService] Error marking notification as read:', error);
        return of(null);
      })
    );
  }

  markAsUnread(notificationId: string): Observable<NotificationDetail | null> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.warn('[NotificationService] No token available, cannot mark as unread');
          return of(null);
        }
        return this.http.patch<NotificationDetail>(
          `${this.apiUrl}/notifications/${notificationId}/mark-unread`,
          {},
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      }),
      tap((notification) => {
        if (!this.isWebSocketConnected && notification) {
          const currentCount = this.unreadCountSubject.value;
          this.unreadCountSubject.next(currentCount + 1);
          const notifications = this.notificationsSubject.value;
          const updatedNotifications = notifications.map((n) =>
            n.id === notificationId ? { ...n, isRead: false, readAt: undefined } : n
          );
          this.notificationsSubject.next(updatedNotifications);
        }
      }),
      catchError((error) => {
        console.error('[NotificationService] Error marking notification as unread:', error);
        return of(null);
      })
    );
  }

  markAllAsRead(category?: number): Observable<MarkAllAsReadResult> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.warn('[NotificationService] No token available, cannot mark all as read');
          return of({ markedCount: 0 });
        }

        let params = new HttpParams();
        if (category !== undefined) {
          params = params.set('category', category.toString());
        }

        return this.http.patch<MarkAllAsReadResult>(
          `${this.apiUrl}/notifications/mark-all-as-read`,
          {},
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
            params,
          }
        );
      }),
      tap((result) => {
        if (result.markedCount > 0) {
          const currentCount = this.unreadCountSubject.value;
          this.unreadCountSubject.next(Math.max(0, currentCount - result.markedCount));
          const notifications = this.notificationsSubject.value;
          const updatedNotifications = notifications.map((n) => ({ ...n, isRead: true }));
          this.notificationsSubject.next(updatedNotifications);
        }
      }),
      catchError((error) => {
        console.error('[NotificationService] Error marking all as read:', error);
        return of({ markedCount: 0 });
      })
    );
  }

  archiveNotification(notificationId: string): Observable<NotificationDetail | null> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.warn('[NotificationService] No token available, cannot archive notification');
          return of(null);
        }
        return this.http.delete<NotificationDetail>(
          `${this.apiUrl}/notifications/${notificationId}/archive`,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      }),
      tap((notification) => {
        if (notification) {
          const notifications = this.notificationsSubject.value;
          const updatedNotifications = notifications.filter((n) => n.id !== notificationId);
          this.notificationsSubject.next(updatedNotifications);
        }
      }),
      catchError((error) => {
        console.error('[NotificationService] Error archiving notification:', error);
        return of(null);
      })
    );
  }

  batchActions(request: BatchNotificationActionRequest): Observable<BatchOperationResult> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.warn('[NotificationService] No token available, cannot perform batch action');
          return of({
            successCount: 0,
            failedCount: request.notificationIds.length,
            failedIds: request.notificationIds,
          });
        }
        const actionMap: Record<string, number> = {
          'MARK_READ': BatchNotificationAction.MARK_READ,
          'ARCHIVE': BatchNotificationAction.ARCHIVE,
          'MARK_UNREAD': BatchNotificationAction.MARK_UNREAD,
        };
        const payload = {
          notificationIds: request.notificationIds,
          action: actionMap[request.action],
        };
        return this.http.post<BatchOperationResult>(
          `${this.apiUrl}/notifications/batch-actions`,
          payload,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      }),
      tap((result) => {
        if (result.successCount > 0) {
          if (request.action === 'MARK_READ') {
            const currentCount = this.unreadCountSubject.value;
            this.unreadCountSubject.next(Math.max(0, currentCount - result.successCount));
          } else if (request.action === 'MARK_UNREAD') {
            const currentCount = this.unreadCountSubject.value;
            this.unreadCountSubject.next(currentCount + result.successCount);
          }
          this.refresh();
        }
      }),
      catchError((error) => {
        console.error('[NotificationService] Error performing batch action:', error);
        return of({
          successCount: 0,
          failedCount: request.notificationIds.length,
          failedIds: request.notificationIds,
        });
      })
    );
  }

  startAutoRefresh(): void {
    if (this.autoRefresh$) {
      return;
    }

    this.connectWebSocket();

    this.autoRefresh$ = timer(3000, this.autoRefreshInterval).pipe(
      switchMap(() => {
        if (this.isWebSocketConnected) {
          return of({ unreadCount: this.unreadCountSubject.value });
        }
        return this.getNotificationCount();
      }),
      tap((response) => {
        const currentCount = this.unreadCountSubject.value;
        if (response.unreadCount > currentCount && !this.isWebSocketConnected) {
          this.getNotifications(1, 10, this.currentFilters).subscribe();
        }
      }),
      shareReplay(1)
    );
    this.autoRefresh$.subscribe({
      error: (err) => console.error('[NotificationService] Auto-refresh error:', err),
    });
  }

  stopAutoRefresh(): void {
    this.disconnectWebSocket();
    this.autoRefresh$ = undefined;
  }

  updateSettings(settings: Parameters<typeof this.webSocketService.updateSettings>[0]): void {
    this.webSocketService.updateSettings(settings);
  }

  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  getCurrentNotifications(): Notification[] {
    return this.notificationsSubject.value;
  }

  refresh(): void {
    this.getNotificationCount().subscribe();
    this.getNotifications(1, 10, this.currentFilters).subscribe();
  }

  getTags(): Observable<{ tags: Tag[]; popularTags: Tag[] }> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of({ tags: [], total: 0 } as TagListResponse);
        }
        return this.http.get<TagListResponse>(`${this.apiUrl}/tags`, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      }),
      map((response) => {
        const allTags = response.tags || [];
        const popularTags = allTags.filter((tag) => tag.isSystemTag);
        return {
          tags: allTags,
          popularTags: popularTags,
        };
      }),
      catchError(() => {
        return of({ tags: [], popularTags: [] });
      })
    );
  }

  togglePin(notificationId: string): Observable<PinNotificationResult | null> {
    const notifications = this.notificationsSubject.value;
    const notification = notifications.find((n) => n.id === notificationId);
    if (!notification) {
      return of(null);
    }

    const newPinnedState = !notification.isPinned;

    const updatedNotifications = notifications.map((n) =>
      n.id === notificationId ? { ...n, isPinned: newPinnedState } : n
    );
    this.notificationsSubject.next(updatedNotifications);

    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          this.notificationsSubject.next(notifications);
          return of(null);
        }
        return this.http.patch<PinNotificationResult>(
          `${this.apiUrl}/notifications/${notificationId}/pin`,
          {},
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      }),
      tap((result) => {
        if (result) {
          const syncedNotifications = this.notificationsSubject.value.map((n) =>
            n.id === notificationId ? { ...n, isPinned: result.isPinned } : n
          );
          this.notificationsSubject.next(syncedNotifications);
        }
      }),
      catchError((error) => {
        console.error('[NotificationService] Error toggling pin:', error);
        this.notificationsSubject.next(notifications);
        return throwError(() => error);
      })
    );
  }

  postponeNotification(
    notificationId: string,
    days: number
  ): Observable<PostponeNotificationResult | null> {
    const currentNotifications = this.notificationsSubject.value;
    const notificationIndex = currentNotifications.findIndex((n) => n.id === notificationId);

    if (notificationIndex === -1) {
      return of(null);
    }

    const notification = currentNotifications[notificationIndex];

    const postponedUntil = new Date();
    postponedUntil.setDate(postponedUntil.getDate() + days);

    const updatedNotification = {
      ...notification,
      isPostponed: true,
      isRead: true,
      postponedUntil: postponedUntil.toISOString(),
    };
    const updatedNotifications = [...currentNotifications];
    updatedNotifications[notificationIndex] = updatedNotification;
    this.notificationsSubject.next(updatedNotifications);

    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          this.notificationsSubject.next(currentNotifications);
          return of(null);
        }
        return this.http.post<PostponeNotificationResult>(
          `${this.apiUrl}/notifications/${notificationId}/postpone`,
          { days },
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      }),
      tap((result) => {
        if (result) {
          const finalNotifications = this.notificationsSubject.value;
          const finalIndex = finalNotifications.findIndex((n) => n.id === notificationId);
          if (finalIndex !== -1) {
            const updatedNotifications = finalNotifications.map((n, idx) =>
              idx === finalIndex
                ? { ...n, isPostponed: result.isPostponed, postponedUntil: result.postponedUntil }
                : n
            );
            this.notificationsSubject.next(updatedNotifications);
          }
          this.postponementChangedSubject.next();
          this.webSocketService.broadcastPostponementChanged();
        }
      }),
      switchMap((result) => result ? this.getNotificationCount().pipe(map(() => result)) : of(null)),
      catchError((error) => {
        console.error('[NotificationService] Error postponing notification:', error);
        this.notificationsSubject.next(currentNotifications);
        return of(null);
      })
    );
  }

  cancelPostponement(notificationId: string): Observable<CancelPostponeResponse | null> {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map((n) =>
      n.id === notificationId ? { ...n, isPostponed: false, postponedUntil: null } : n
    );
    this.notificationsSubject.next(updatedNotifications);

    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          this.notificationsSubject.next(currentNotifications);
          return of(null);
        }
        return this.http.delete<CancelPostponeResponse>(
          `${this.apiUrl}/notifications/${notificationId}/postpone`,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      }),
      tap(() => {
        this.postponementChangedSubject.next();
        this.webSocketService.broadcastPostponementChanged();
      }),
      switchMap((result) => this.getNotificationCount().pipe(map(() => result))),
      catchError((error) => {
        console.error('[NotificationService] Error cancelling postponement:', error);
        this.notificationsSubject.next(currentNotifications);
        return of(null);
      })
    );
  }

  getPostponedCount(): number {
    return this.notificationsSubject.value.filter((n) => n.isPostponed).length;
  }

  getSummary(): Observable<NotificationsSummary> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return of({ totalUnread: 0, totalPostponed: 0, totalPinned: 0 });
        }
        return this.http.get<NotificationsSummary>(`${this.apiUrl}/notifications/summary`, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      }),
      catchError((error) => {
        console.error('[NotificationService] Error fetching summary:', error);
        return of({ totalUnread: 0, totalPostponed: 0, totalPinned: 0 });
      })
    );
  }

  parseTranslations(json: string | null | undefined): NotificationTranslations | null {
    if (!json) {
      return null;
    }

    try {
      const parsed = JSON.parse(json);
      if (typeof parsed === 'object' && parsed !== null && parsed.es) {
        return parsed as NotificationTranslations;
      }
      console.warn('[NotificationService] translationsJson missing required "es" locale');
      return null;
    } catch (error) {
      console.error('[NotificationService] Error parsing translationsJson:', error);
      return null;
    }
  }

  getTranslationForLocale(
    translations: NotificationTranslations | null,
    locale: string
  ): NotificationTranslation | null {
    if (!translations) {
      return null;
    }

    const localeMap: Record<string, string> = {
      'cht': 'zh',
    };

    const normalizedLocale = locale.toLowerCase();
    const backendLocale = localeMap[normalizedLocale] || normalizedLocale;
    const requestedTranslation = translations[backendLocale];
    if (requestedTranslation) {
      return requestedTranslation;
    }
    if (translations['en']) {
      return translations['en'];
    }

    return null;
  }

  private extractTitle(translation: NotificationTranslation): string {
    if ('title' in translation && translation.title) {
      return translation.title;
    }
    const pascalTranslation = translation as unknown as { Title?: string };
    if (pascalTranslation.Title) {
      return pascalTranslation.Title;
    }
    return '';
  }

  private extractMessage(translation: NotificationTranslation): string {
    if ('message' in translation && translation.message) {
      return translation.message;
    }
    const pascalTranslation = translation as unknown as { Message?: string };
    if (pascalTranslation.Message) {
      return pascalTranslation.Message;
    }
    return '';
  }

  getLocalizedTitle(dispatch: { translationsJson?: string | null; renderedTitle?: string } | undefined | null, locale: string): string {
    if (!dispatch) {
      return 'Notificación sin traducción';
    }
    if (dispatch.translationsJson) {
      const translations = this.parseTranslations(dispatch.translationsJson);
      const translation = this.getTranslationForLocale(translations, locale);
      if (translation) {
        const title = this.extractTitle(translation);
        if (title) {
          return title;
        }
      }
    }
    if (dispatch.renderedTitle) {
      return dispatch.renderedTitle;
    }

    return 'Notificación sin traducción';
  }

  getLocalizedMessage(dispatch: { translationsJson?: string | null; renderedMessage?: string } | undefined | null, locale: string): string {
    if (!dispatch) {
      return '';
    }
    if (dispatch.translationsJson) {
      const translations = this.parseTranslations(dispatch.translationsJson);
      const translation = this.getTranslationForLocale(translations, locale);
      if (translation) {
        const message = this.extractMessage(translation);
        if (message) {
          return message;
        }
      }
    }
    if (dispatch.renderedMessage) {
      return dispatch.renderedMessage;
    }

    return '';
  }
}
